import { useIntl } from 'react-intl'
import { useCallback } from 'react'
import styled from 'styled-components'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import CruiseLoyaltyPoints from 'components/Cruises/CruiseLoyaltyPoints'
import AgentCommissionPanel from 'agentHub/components/AgentCommissionPanel'
import productLabels from 'i18n/productLabels'
import useCruiseBundleFilter from 'hooks/Cruise/useCruiseBundleFilter'
import type { CruiseOverviewSinglePriceProps } from './CruiseOverviewSinglePrice'
import CruiseOverviewPriceDetails from './CruiseOverviewPriceDetails'

const Columns = styled.div`
  min-width: 0;

  > div {
    max-width: 100%;
  }
`

function CruiseOverviewBundlePrice({ view, hasValidDepartureDates, showPackageOptions }: CruiseOverviewSinglePriceProps) {
  const intl = useIntl()
  const { setBundleFilterEnabled } = useCruiseBundleFilter()
  const showCruiseOptions = useCallback(() => {
    setBundleFilterEnabled(false)
    showPackageOptions()
  }, [setBundleFilterEnabled, showPackageOptions])
  const showBundleOptions = useCallback(() => {
    setBundleFilterEnabled(true)
    showPackageOptions()
  }, [setBundleFilterEnabled, showPackageOptions])
  const bundleTitle = intl.formatMessage({
    id: 'cvEFh6JhsUZGNK13g7zl',
    defaultMessage: 'Cruise + Hotel',
    description: 'Heading for the combined cruise and hotel price in the cruise offer overview',
  })
  const availabilityLabel = intl.formatMessage({
    id: 'jz0dx7BQ7GSRVwQkkFjH',
    defaultMessage: 'Check availability',
    description: 'Button text to check cruise availability in the cruise section',
  })
  const displayedPrice = view.lowestPrice.promotionalPrice ?? view.lowestPrice.primaryPrice

  if (!view.lowestBundlePrice) return null

  return <Group direction="vertical" gap={16}>
    <Columns>
      <AccommodationPackageTilePricingBlocks>
        <Group
          direction="vertical"
          gap={12}
          fullHeight
          verticalAlign="space-between"
        >
          <Group direction="vertical" gap={12}>
            <Heading variant="heading5" as="h3">{intl.formatMessage(productLabels.cruise)}</Heading>
            <CruiseOverviewPriceDetails view={view} hasValidDepartureDates={hasValidDepartureDates} priceType="standard" />
          </Group>
          <TextButton
            kind="secondary"
            onClick={showCruiseOptions}
            fit="full-width"
          >
            {availabilityLabel}
          </TextButton>
        </Group>
        <Group
          direction="vertical"
          gap={12}
          fullHeight
          verticalAlign="space-between"
        >
          <Group direction="vertical" gap={12}>
            <Heading variant="heading5" as="h3">{bundleTitle}</Heading>
            <CruiseOverviewPriceDetails view={view} hasValidDepartureDates={hasValidDepartureDates} priceType="bundle" />
          </Group>
          <TextButton
            kind="secondary"
            onClick={showBundleOptions}
            fit="full-width"
          >
            {availabilityLabel}
          </TextButton>
        </Group>
      </AccommodationPackageTilePricingBlocks>
    </Columns>
    <AgentCommissionPanel
      offerId={view.offer.id}
      offer={view.offer}
      offerTotal={displayedPrice.total}
      retailValue={view.discountPills?.price ?? 0}
    />
    <CruiseLoyaltyPoints offer={view.offer} calculationType="estimate" />
  </Group>
}

export default CruiseOverviewBundlePrice
